import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  Bookmark,
  BookmarkCheck,
  BriefcaseBusiness,
  MapPin,
  MessageCircle,
  RotateCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';
import { STATIC_JOBS, STATIC_JOBS_VERIFIED_AT } from './data/staticJobs';
import JobApplyDialog from './JobApplyDialog';
import { useJobInteractions } from './jobInteractions';

const ALL = 'all';

const formatPay = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(amount);

const buildConciergePrompt = (job) => (
  `Find me approved ${job.specialty} jobs in ${job.state} paying at least ${formatPay(job.weeklyGross)} gross per week.`
);

const JobCard = ({ job, isSaved, applicationStarted, onToggleSaved, onApply }) => {
  const conciergeUrl = `/concierge?message=${encodeURIComponent(buildConciergePrompt(job))}`;

  return (
    <article
      data-testid={`job-card-${job.id}`}
      className="group flex h-full flex-col rounded-2xl border border-[#E2E1DC] bg-white p-5 shadow-[0_12px_36px_rgba(44,40,37,0.06)] transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-[#C9C7BF] hover:shadow-[0_18px_42px_rgba(44,40,37,0.10)] sm:p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex min-h-7 items-center gap-1.5 rounded-full bg-[#EEF5F0] px-2.5 py-1 text-xs font-semibold text-[#2F6B52]">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} />
              source checked
            </span>
            <span className="rounded-full bg-[#F3F2EE] px-2.5 py-1 text-xs text-[#6B6A68]">
              {job.employmentType}
            </span>
          </div>
          <h2 className="text-xl font-semibold leading-snug tracking-[-0.015em] text-[#2C2825]">
            {job.title}
          </h2>
        </div>
        <div className="flex shrink-0 items-start gap-2">
          <button
            type="button"
            onClick={() => onToggleSaved(job.id)}
            aria-label={isSaved ? `Remove ${job.title} from saved jobs` : `Save ${job.title}`}
            aria-pressed={isSaved}
            data-testid={`save-job-${job.id}`}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E0DED8] bg-white text-[#5E5A56] transition-[background-color,border-color,color] duration-200 hover:border-[#C8C4BC] hover:bg-[#F5F3EE] hover:text-[#2C2825] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D8A79]/40"
          >
            {isSaved ? <BookmarkCheck className="h-5 w-5 text-[#2F6B52]" /> : <Bookmark className="h-5 w-5" />}
          </button>
          <div className="text-right">
            <div className="text-xl font-semibold tabular-nums text-[#2C2825]">
              {formatPay(job.weeklyGross)}
            </div>
            <div className="mt-0.5 text-xs text-[#77736F]">gross / week</div>
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-2.5 text-sm text-[#5E5A56]">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-[#85817D]" strokeWidth={1.8} />
          <span>{job.city}, {job.state}</span>
        </div>
        <div className="flex items-center gap-2">
          <BriefcaseBusiness className="h-4 w-4 shrink-0 text-[#85817D]" strokeWidth={1.8} />
          <span>{job.specialty} · {job.shift || 'shift not verified'}</span>
        </div>
      </div>

      <div className="mt-auto pt-6">
        <div className="mb-4 border-t border-[#ECEBE7] pt-4 text-xs leading-relaxed text-[#85817D]">
          Care Team Solutions · supplied through Staffing Referrals
          {applicationStarted && (
            <span className="mt-2 flex items-center gap-1.5 font-semibold text-[#2F6B52]">
              <ArrowUpRight className="h-3.5 w-3.5" /> Application started
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={() => onApply(job)} className="h-11 flex-1 rounded-xl bg-[#2C2825] px-4 text-sm text-white shadow-none hover:bg-[#1F1C1A]" data-testid={`apply-job-${job.id}`}>
            {applicationStarted ? 'Continue application' : 'Apply with Care Team'} <ArrowUpRight className="h-4 w-4" />
          </Button>
          <Button asChild variant="outline" className="h-11 flex-1 rounded-xl border-[#D8D6CF] bg-white px-4 text-sm text-[#3B3835] shadow-none hover:bg-[#F5F3EE]">
            <Link to={`/jobs/${job.id}`} data-testid={`job-detail-link-${job.id}`}>
              View details
            </Link>
          </Button>
        </div>
        <Link to={conciergeUrl} className="mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-[#4C6A59] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D8A79]/40" data-testid={`job-match-link-${job.id}`}>
          Ask Alex for similar approved jobs <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
};

export default function JobBoard() {
  const [query, setQuery] = useState('');
  const [profession, setProfession] = useState(ALL);
  const [state, setState] = useState(ALL);
  const [minimumPay, setMinimumPay] = useState(ALL);
  const [savedOnly, setSavedOnly] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const {
    savedJobIds,
    startedApplicationIds,
    toggleSavedJob,
    markApplicationStarted,
  } = useJobInteractions();

  useEffect(() => {
    const priorTitle = document.title;
    document.title = 'Care Team jobs | HeyTraveler';
    return () => { document.title = priorTitle; };
  }, []);

  const professions = useMemo(
    () => [...new Set(STATIC_JOBS.map((job) => job.profession))].sort(),
    [],
  );
  const states = useMemo(
    () => [...new Set(STATIC_JOBS.map((job) => job.state))].sort(),
    [],
  );

  const jobs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const payFloor = minimumPay === ALL ? 0 : Number(minimumPay);

    return STATIC_JOBS.filter((job) => {
      const searchable = [
        job.title,
        job.profession,
        job.specialty,
        job.city,
        job.state,
      ].join(' ').toLowerCase();

      return (!normalizedQuery || searchable.includes(normalizedQuery))
        && (profession === ALL || job.profession === profession)
        && (state === ALL || job.state === state)
        && (!savedOnly || savedJobIds.has(job.id))
        && job.weeklyGross >= payFloor;
    }).sort((a, b) => b.weeklyGross - a.weeklyGross);
  }, [minimumPay, profession, query, savedJobIds, savedOnly, state]);

  const hasFilters = Boolean(query || profession !== ALL || state !== ALL || minimumPay !== ALL || savedOnly);
  const resetFilters = () => {
    setQuery('');
    setProfession(ALL);
    setState(ALL);
    setMinimumPay(ALL);
    setSavedOnly(false);
  };

  return (
    <main className="min-h-screen min-h-[100dvh] bg-[#F8F7F3] text-[#2C2825]">
      <header className="border-b border-[#E5E3DD] bg-[#F8F7F3]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="font-serif text-2xl lowercase tracking-[-0.02em] text-[#2C2825]" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>
            hey, traveler.
          </Link>
          <Button asChild variant="outline" className="h-10 rounded-xl border-[#D8D6CF] bg-white px-4 text-[#3B3835] shadow-none hover:bg-[#F0EFEA]">
            <Link to="/concierge"><MessageCircle className="h-4 w-4" /> Talk to Alex</Link>
          </Button>
        </div>
      </header>

      <section className="border-b border-[#E5E3DD] bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#EEF5F0] px-3 py-1.5 text-xs font-semibold text-[#2F6B52]">
              <ShieldCheck className="h-4 w-4" strokeWidth={2} />
              Care Team source snapshot
            </div>
            <h1 className="font-serif text-4xl leading-[1.05] tracking-[-0.025em] text-[#2C2825] sm:text-5xl lg:text-6xl" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>
              Find an assignment without volunteering for recruiter chaos.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-[#625E5A] sm:text-lg">
              Browse a small Care Team Solutions snapshot, then let Alex monitor the criteria that actually matter to you.
            </p>
          </div>

          <div className="mt-7 flex max-w-3xl items-start gap-3 rounded-2xl border border-[#E5E3DD] bg-[#F8F7F3] p-4 text-sm leading-6 text-[#625E5A]">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#2F6B52]" strokeWidth={1.8} />
            <p>
              <strong className="font-semibold text-[#3B3835]">Testing snapshot:</strong> these listings were source-checked on {STATIC_JOBS_VERIFIED_AT}. They are not yet part of Alex&apos;s approved alert inventory. Availability and pay can change; verify the Care Team source before acting.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <div className="rounded-2xl border border-[#E2E1DC] bg-white p-4 shadow-[0_10px_30px_rgba(44,40,37,0.05)] sm:p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-[#3B3835]">
            <SlidersHorizontal className="h-4 w-4 text-[#74706C]" strokeWidth={1.8} />
            Narrow the list
          </div>
          <div className="grid gap-3 md:grid-cols-[minmax(240px,1.6fr)_1fr_0.8fr_0.9fr]">
            <label className="relative block">
              <span className="sr-only">Search jobs</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#85817D]" strokeWidth={1.8} />
              <Input
                data-testid="job-search-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="specialty, city, or state"
                className="h-11 rounded-xl border-[#D8D6CF] bg-[#FBFAF7] pl-10 text-base shadow-none placeholder:text-[#96918C] focus-visible:ring-2 focus-visible:ring-[#6D8A79]/30 md:text-base"
              />
            </label>

            <Select value={profession} onValueChange={setProfession}>
              <SelectTrigger data-testid="profession-filter" aria-label="Filter by profession" className="h-11 rounded-xl border-[#D8D6CF] bg-[#FBFAF7] text-base shadow-none focus:ring-2 focus:ring-[#6D8A79]/30">
                <SelectValue placeholder="Profession" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All professions</SelectItem>
                {professions.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={state} onValueChange={setState}>
              <SelectTrigger data-testid="state-filter" aria-label="Filter by state" className="h-11 rounded-xl border-[#D8D6CF] bg-[#FBFAF7] text-base shadow-none focus:ring-2 focus:ring-[#6D8A79]/30">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All states</SelectItem>
                {states.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={minimumPay} onValueChange={setMinimumPay}>
              <SelectTrigger data-testid="pay-filter" aria-label="Filter by minimum weekly gross pay" className="h-11 rounded-xl border-[#D8D6CF] bg-[#FBFAF7] text-base shadow-none focus:ring-2 focus:ring-[#6D8A79]/30">
                <SelectValue placeholder="Minimum pay" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any weekly pay</SelectItem>
                <SelectItem value="2000">$2,000+ / week</SelectItem>
                <SelectItem value="2250">$2,250+ / week</SelectItem>
                <SelectItem value="2500">$2,500+ / week</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mb-5 mt-8 flex flex-wrap items-center justify-between gap-3">
          <p aria-live="polite" className="text-sm text-[#6B6763]">
            <span className="font-semibold tabular-nums text-[#34312E]">{jobs.length}</span> source-checked {jobs.length === 1 ? 'listing' : 'listings'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setSavedOnly((current) => !current)}
              aria-pressed={savedOnly}
              className={`inline-flex min-h-11 items-center gap-2 rounded-xl border px-3 text-sm font-medium transition-[background-color,border-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D8A79]/40 ${savedOnly ? 'border-[#8EAA99] bg-[#EAF3ED] text-[#2F6B52]' : 'border-[#D8D6CF] bg-white text-[#5E5A56] hover:bg-[#F5F3EE]'}`}
              data-testid="saved-only-filter"
            >
              {savedOnly ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
              Saved ({savedJobIds.size})
            </button>
            {hasFilters && (
              <button type="button" onClick={resetFilters} className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-medium text-[#5E5A56] underline-offset-4 hover:text-[#2C2825] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D8A79]/40" data-testid="clear-filters-button">
                <RotateCcw className="h-4 w-4" strokeWidth={1.8} /> Clear filters
              </button>
            )}
          </div>
        </div>

        {jobs.length > 0 ? (
          <div className="grid gap-5 lg:grid-cols-2" data-testid="job-results">
            {jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                isSaved={savedJobIds.has(job.id)}
                applicationStarted={startedApplicationIds.has(job.id)}
                onToggleSaved={toggleSavedJob}
                onApply={setSelectedJob}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-[#CFCBC4] bg-white px-5 py-14 text-center" data-testid="job-empty-state">
            <Search className="mx-auto h-7 w-7 text-[#85817D]" strokeWidth={1.5} />
            <h2 className="mt-4 text-xl font-semibold text-[#34312E]">Nothing in this small snapshot matches.</h2>
            <p className="mx-auto mt-2 max-w-lg text-base leading-7 text-[#6B6763]">
              This does not mean the job does not exist. Clear the filters or tell Alex what you want monitored.
            </p>
            <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
              <Button onClick={resetFilters} variant="outline" className="h-11 rounded-xl border-[#D8D6CF] bg-white px-5 shadow-none hover:bg-[#F5F3EE]">
                Clear filters
              </Button>
              <Button asChild className="h-11 rounded-xl bg-[#2C2825] px-5 text-white shadow-none hover:bg-[#1F1C1A]">
                <Link to="/concierge">Tell Alex what to watch</Link>
              </Button>
            </div>
          </div>
        )}

        <aside className="mt-10 overflow-hidden rounded-2xl bg-[#24211F] px-5 py-7 text-white sm:flex sm:items-center sm:justify-between sm:gap-8 sm:px-8 sm:py-8">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold text-[#CFC8BF]">Don&apos;t see the right one?</div>
            <h2 className="mt-2 font-serif text-3xl tracking-[-0.02em] sm:text-4xl" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>
              Give Alex the assignment, not your attention.
            </h2>
            <p className="mt-3 text-base leading-7 text-[#D8D2CB]">
              Set your specialty, location, gross weekly floor, shift, and timing. Alex will only alert you after a strict match is verified.
            </p>
          </div>
          <Button asChild className="mt-6 h-12 shrink-0 rounded-xl bg-white px-5 text-[#24211F] shadow-none hover:bg-[#F0EFEA] sm:mt-0">
            <Link to="/concierge">Set my watch <ArrowUpRight className="h-4 w-4" /></Link>
          </Button>
        </aside>

        <div className="mt-8 flex flex-col gap-3 border-t border-[#E0DED8] pt-6 text-xs leading-relaxed text-[#77736F] sm:flex-row sm:items-center sm:justify-between">
          <p>HeyTraveler currently searches only approved Care Team Solutions opportunities supplied through Staffing Referrals.</p>
          <Link to="/privacy" className="shrink-0 underline underline-offset-4 hover:text-[#3B3835]">Privacy policy</Link>
        </div>
      </section>
      <JobApplyDialog
        job={selectedJob}
        open={Boolean(selectedJob)}
        onOpenChange={(open) => { if (!open) setSelectedJob(null); }}
        onApplicationStarted={markApplicationStarted}
      />
    </main>
  );
}
